import { useEffect, useState } from "react";
import PricePolicy from "../components/pricepolicy/PricePolicy";
import { useCookies } from "react-cookie";
import { PaymentData } from "../components/common/Common";
import Upgrade from "../module/Upgrade";
import DataProvider from "../data_provider/DataProvider";

import { useModal } from "../components/modal/Modal";
import ModalButtons from "../components/modal/ModalButtons";
import { Helmet } from "react-helmet-async";
import LoginForm from "../components/login/LoginForm";
import SignUpModal from "../components/login/SignUpModal";
import { useLoginStatus } from "../hooks/useLoginStatus";

function PricePolicyScreen() {
    const [cookies] = useCookies(["KMNM", "KMUI", "KMLN"]);
    const { ModalComponent, openModal, closeModal } = useModal();

    // 25.08.28 | Kim Young Jun | 실시간 로그인 상태 및 구독 등급 관리를 위한 상태 변수
    // isLoggedIn: 로그인 여부, userGrade: 사용자 등급 (1: 일반, 2: 프로)
    // 사용자 로그인 및 구독 상태 관리
    const isLogin = useLoginStatus(); // 로그인감지 방식 변경 | smyun | 250905
    const [userGrade, setUserGrade] = useState<number>(1); // 1: 일반, 2: 프로

    const showUpgradeResultModal = (title: string, content: string) => {
        openModal({
            title,
            children: content,
            modalButtons: null,
        });
    };

    /**
     * 25.08.28 | Kim Young Jun | 로그인 상태 및 구독 정보 실시간 동기화를 위한 사용자 정보 조회 함수
     * API 호출을 통해 현재 로그인 상태와 구독 등급(grade)을 확인하고 상태 업데이트
     */
    // 사용자 정보 조회 함수
    const fetchUserInfo = async () => {
        try {
            const response = await DataProvider.GetUserInfo();
            console.log("GetUserInfo 응답:", response);

            if (response.code === "OK" && response.result) {
                const grade = response.result.grade || 1;
                setUserGrade(grade);
                console.log("사용자 정보 업데이트 - Grade:", grade, "전체 응답:", response.result);
            } else {
                setUserGrade(1);
                console.log("로그인되지 않은 사용자 또는 정보 없음");
            }
        } catch (error) {
            console.log("사용자 정보 조회 실패:", error);
            setUserGrade(1);
        }
    };

    // 로그인 방식 변경으로 불필요 | smyun | 250905
    // 25.08.28 | Kim Young Jun | 탭 간 로그인 상태 실시간 동기화를 위한 이벤트 리스너 등록
    // localStorage 변경 감지 및 커스텀 이벤트를 통한 동일 탭 내 로그인 감지
    // useEffect(() => {
    //     const handleLoginSync = (event: StorageEvent) => {
    //         if (event.key === "loginSync") {
    //             console.log("로그인 동기화 이벤트 감지");
    //             fetchUserInfo();
    //         }
    //     };

    //     // storage 이벤트 리스너 등록
    //     window.addEventListener("storage", handleLoginSync);

    //     // 같은 탭에서의 로그인을 감지하기 위한 커스텀 이벤트 리스너
    //     const handleCustomLoginSync = () => {
    //         console.log("커스텀 로그인 동기화 이벤트 감지");
    //         fetchUserInfo();
    //     };

    //     window.addEventListener("loginSyncCustom", handleCustomLoginSync);

    //     return () => {
    //         window.removeEventListener("storage", handleLoginSync);
    //         window.removeEventListener("loginSyncCustom", handleCustomLoginSync);
    //     };
    // }, []);

    // 초기 사용자 정보 조회
    useEffect(() => {
        fetchUserInfo();
    }, []);

    // 로그인 방식 변경으로 감지 신호 수신 방법 통일 | smyun | 250905
    // 로그인 여부 바뀌면 재조회
    useEffect(() => {
        if (isLogin) {
            // 로그인 직후 세션/쿠키 전파 지연 흡수용 소폭 딜레이
            const t = setTimeout(fetchUserInfo, 200);
            return () => clearTimeout(t);
        } else {
            setUserGrade(1);
        }
    }, [isLogin]);

    // 탭 신호 수신
    useEffect(() => {
        const LS_KEY = "auth:event";
        const BC_NAME = "auth-channel";

        const onStorage = (e: StorageEvent) => {
            if (e.key === LS_KEY) fetchUserInfo();
        };
        window.addEventListener("storage", onStorage);

        let bc: BroadcastChannel | null = null;
        try {
            bc = new BroadcastChannel(BC_NAME);
            bc.onmessage = () => fetchUserInfo();
        } catch {}

        return () => {
            window.removeEventListener("storage", onStorage);
            bc?.close();
        };
    }, []);

    useEffect(() => {
        const iamport = document.createElement("script");
        const jquery = document.createElement("script");
        iamport.src = "https://cdn.iamport.kr/js/iamport.payment-1.1.7.js";
        jquery.src = "https://code.jquery.com/jquery-1.12.4.min.js";
        document.head.appendChild(iamport);
        document.head.appendChild(jquery);
        return () => {
            document.head.removeChild(iamport);
            document.head.removeChild(jquery);
        };
    }, []);

    // 25.08.28 | Kim Young Jun | 구독 버튼 클릭 시 로그인 상태 및 구독 상태에 따른 분기 처리
    // 미로그인 -> 로그인 모달, 구독 중 -> 안내 메시지, 일반 사용자 -> 결제 진행
    const onClickUpgrade = () => {
        // 로그인하지 않은 경우
        if (!isLogin) {
            openModal({
                title: "안내",
                children: (
                    <>
                        로그인이 필요한 서비스입니다.
                        <br />
                        로그인을 하시겠습니까?
                    </>
                ),
                modalButtons: (
                    <ModalButtons
                        buttonCount={2}
                        buttons={[
                            {
                                label: "취소",
                                color: "Secondary",
                                onClick: () => closeModal(),
                            },
                            {
                                label: "로그인",
                                color: "Primary",
                                onClick: () => {
                                    // 로그인 모달 띄우기
                                    openModal({
                                        title: "로그인",
                                        children: (
                                            <LoginForm
                                                onClose={() => {
                                                    closeModal();
                                                    // 로그인 성공 후 사용자 정보 재조회
                                                    setTimeout(() => {
                                                        fetchUserInfo();
                                                    }, 500);
                                                }}
                                                onClickSignUp={onClickSignUp}
                                            />
                                        ),
                                        modalButtons: null,
                                        size: "wide",
                                        disableOverlayClose: true,
                                    });
                                },
                            },
                        ]}
                    />
                ),
            });
            return;
        }

        // 이미 프로 버전 구독 중인 경우
        if (userGrade >= 2) {
            openModal({
                title: "구독 중",
                children: "이미 프로 버전을 구독 중입니다.",
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => closeModal(),
                            },
                        ]}
                    />
                ),
            });
            return;
        }

        // 일반 사용자인 경우 결제 진행
        const data: PaymentData = {
            name: cookies.KMNM,
            email: cookies.KMUI,
        };

        openModal({
            title: "결제 안내",
            children: "모두의지도 프로버전 결제를 진행하시겠습니까?",
            modalButtons: (
                <ModalButtons
                    buttonCount={2}
                    buttons={[
                        {
                            label: "취소",
                            color: "Secondary",
                            onClick: () => closeModal(),
                        },
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => {
                                // closeModal();
                                Upgrade(data, {
                                    onSuccess: () => {
                                        setUserGrade(2); // 결제 성공 시 상태 업데이트
                                        showUpgradeResultModal("성공", "결제가 성공적으로 완료되었습니다.");
                                    },
                                    onError: (msg) => showUpgradeResultModal("안내", msg),
                                });
                            },
                        },
                    ]}
                />
            ),
        });
    };

    const onClickSignUp = () => {
        openModal({
            title: "회원가입",
            children: <SignUpModal onClose={closeModal} />,
            modalButtons: null,
            size: "normal",
        });
    };

    return (
        <div>
            <ModalComponent />
            <Helmet>
                <title>멤버십 - 모두의지도</title>
                <meta property="og:type" content="website" />
                <meta name="title" property="og:title" content="멤버십 - 모두의지도" />
                <meta
                    name="description"
                    property="og:description"
                    content="멤버십에 가입하여 더욱 많아진 데이터 처리와 다양한 분석 기능을 사용해보세요"
                />
                <meta name="imgage" property="og:image" content="https://modoomap.com/price.jpg" />
                <meta name="url" property="og:url" content="https://modoomap.com/price" />
            </Helmet>
            <PricePolicy onClick={onClickUpgrade} isLoggedIn={isLogin} isSubscribed={userGrade >= 2} />
        </div>
    );
}

export default PricePolicyScreen;
